import { FEATURES, SEARCH_GROUPS } from '@constants/Text'
import { lazyLoadView } from '@utils/LazyLoad'

export const checkPermission = ({ user, show }) => {
  return (
    (user.IS_INTERNAL && show[FEATURES.COMISSOES_INTERNAL]) ||
    (!user.IS_INTERNAL && show[FEATURES.COMISSOES])
  )
}

// `permission: ['gestor']` NÃO bloqueia: em `src/router/index.js` o `can_show` entra
// como OR (`isForType || isForRole || hasPermission`), então um não-gestor de escritório
// COM a flag passaria pelo guard. `customPermission` é o único AND-gate real — retornar
// `false` aqui resulta em 404, e é o que garante o AC-25.
export const checkPermissionRoa = ({ user, show }) => {
  return (
    checkPermission({ user, show }) &&
    !!user.IS_GESTOR &&
    !!show[FEATURES.COMISSOES_MANAGER] &&
    !!show[FEATURES.COMISSOES_ROA]
  )
}

// Mesmo racional do gate acima — `can_show` sozinho NÃO bloqueia, porque em
// `src/router/index.js` ele entra como OR. A aba de IA precisa das DUAS
// condições: acesso ao módulo de comissões E a flag `IA - Comissões`, que é a
// MESMA que o backend exige na rota `/comissoes/chat`. Divergir aqui produz o
// pior dos casos — botão visível que devolve 403.
export const checkPermissionIa = ({ user, show }) => {
  return !!checkPermission({ user, show }) && !!show[FEATURES.IA_COMISSOES]
}

export default [
  {
    path: '/comissoes',
    name: 'comissoes.geral',
    redirect: '/comissoes/visao-geral',
    meta: {
      customPermission: checkPermission,
      // internal: true,
      search: {
        title: 'Comissões',
        description: 'Regras, fechamentos e pagamentos de comissão',
        keywords: ['comissões', 'repasse', 'fechamento', 'pagamento', 'regras'],
        icon: 'coin',
        group: SEARCH_GROUPS.GESTAO_ESCRITORIOS,
      },
    },
    component: () =>
      lazyLoadView(
        import(
          '@modules/comissoes/views/comissoes-nav-bar/comissoes-nav-bar.vue'
        )
      ),
    children: [
      {
        path: 'visao-geral',
        name: 'comissoes.visao-geral',
        redirect: '/comissoes/visao-geral/indicadores',
        meta: {
          can_show: FEATURES.COMISSOES_OVERVIEW,
          // internal: true,
          search: {
            title: 'Comissões — Visão Geral',
            description: 'Indicadores de comissão por profissional e categoria',
            keywords: ['comissões', 'visão geral', 'indicadores', 'resumo'],
            group: SEARCH_GROUPS.GESTAO_ESCRITORIOS,
          },
        },
        component: () =>
          lazyLoadView(
            import('@modules/comissoes/views/view-overview/view-overview.vue')
          ),
        children: [
          {
            path: 'indicadores',
            name: 'comissoes.visao-geral.overview',
            component: () =>
              lazyLoadView(
                import('@modules/comissoes/views/overview/overview.vue')
              ),
            meta: {
              can_show: FEATURES.COMISSOES_OVERVIEW,
              // internal: true,
            },
          },
          {
            path: 'profissionais',
            name: 'comissoes.visao-geral.profissionais',
            meta: {
              can_show: FEATURES.COMISSOES_OVERVIEW,
              // internal: true,
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/comissoes/views/professionals-overview/professionals-overview.vue'
                )
              ),
          },
          {
            path: 'categorias',
            name: 'comissoes.visao-geral.categorias',
            meta: {
              can_show: FEATURES.COMISSOES_OVERVIEW,
              // internal: true,
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/comissoes/views/categories-overview/categories-overview.vue'
                )
              ),
          },
        ],
      },
      {
        path: 'fechamentos',
        name: 'comissoes.fechamentos',
        // redirect: '/comissoes/fechamentos/apuracoes',
        meta: {
          customPermission: checkPermission,
          search: {
            title: 'Comissões — Fechamentos',
            description: 'Apurações, regras, impostos e ajustes de comissão',
            keywords: [
              'fechamento',
              'apuração',
              'regras',
              'impostos',
              'ajustes',
              'gatilhos',
            ],
            group: SEARCH_GROUPS.GESTAO_ESCRITORIOS,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/comissoes/views/view-fechamentos/view-fechamentos.vue'
            )
          ),
      },
      {
        path: 'fechamentos',
        name: 'comissoes.fechamentos._apuracoes',
        meta: {
          customPermission: checkPermission,
        },
        component: () =>
          lazyLoadView(
            import('@modules/comissoes/views/view-comissoes/view-comissoes.vue')
          ),
        children: [
          {
            path: 'apuracoes',
            name: 'comissoes.fechamentos.apuracoes',
            // Preserva 1:1 a intenção do `if` que vivia no created() do
            // app.vue: "essa página deveria ser mais acessada via mobile e
            // foram feitas algumas alterações para responsividade". A página —
            // não o app inteiro, que era o efeito colateral daquele flag
            // one-shot.
            meta: {
              allowMobile: true,
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/comissoes/views/advisor-commissions/view-advisor-commissions.vue'
                )
              ),
          },
          {
            path: 'planilhas',
            name: 'comissoes.fechamentos.planilhas',
            component: () =>
              lazyLoadView(
                import(
                  '@modules/comissoes/views/spreads-sheet/view-spreads-sheet.vue'
                )
              ),
          },
          {
            path: 'impostos',
            name: 'comissoes.fechamentos.impostos',
            component: () =>
              lazyLoadView(import('@modules/comissoes/views/tax/view-tax.vue')),
          },
          {
            path: 'regras',
            name: 'comissoes.fechamentos.regras',
            component: () =>
              lazyLoadView(
                import('@modules/comissoes/views/rules-tab/rules.vue')
              ),
          },
          {
            path: 'referencias',
            name: 'comissoes.fechamentos.referencias',
            meta: {
              can_show: FEATURES.COMISSOES_BASE_DE_DADOS,
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/comissoes/views/reference-base/view-reference-base.vue'
                )
              ),
          },
          {
            path: 'ajustes',
            name: 'comissoes.fechamentos.ajustes',
            component: () =>
              lazyLoadView(
                import(
                  '@modules/comissoes/views/manual-adjustments/manual-adjustments.vue'
                )
              ),
          },
          {
            path: 'minimos',
            name: 'comissoes.fechamentos.minimos',
            component: () =>
              lazyLoadView(
                import(
                  '@modules/comissoes/views/minimum-commission/view-minimum-commission.vue'
                )
              ),
          },
          {
            path: 'troca_campos',
            name: 'comissoes.fechamentos.troca_campos',
            component: () =>
              lazyLoadView(
                import(
                  '@modules/comissoes/views/field-change/view-field-change.vue'
                )
              ),
          },
          {
            path: 'clientes',
            name: 'comissoes.fechamentos.clientes',
            component: () =>
              lazyLoadView(
                import('@modules/comissoes/views/view-fechamentos/clientes.vue')
              ),
          },
          {
            path: 'listas',
            name: 'comissoes.fechamentos.listas',
            component: () =>
              lazyLoadView(
                import('@modules/comissoes/views/lists/view-lists.vue')
              ),
          },
          {
            path: 'gatilhos',
            name: 'comissoes.fechamentos.gatilhos',
            meta: {
              can_show: FEATURES.COMISSOES_GATILHOS,
            },
            component: () =>
              lazyLoadView(
                import('@modules/comissoes/views/gatilhos/gatilhos.vue')
              ),
          },
        ],
      },
      {
        path: 'pagamentos',
        name: 'comissoes.pagamentos',
        meta: {
          customPermission: checkPermission,
          // internal: true,
          permission: ['gestor'],
          search: {
            title: 'Comissões — Pagamentos',
            description: 'Pagamentos de comissão aos profissionais',
            keywords: ['pagamento', 'repasse', 'comissões', 'profissionais'],
            group: SEARCH_GROUPS.GESTAO_ESCRITORIOS,
          },
        },
        component: () =>
          lazyLoadView(
            import('@modules/comissoes/views/view-payment/view-payment.vue')
          ),
      },
      {
        path: 'roa',
        name: 'comissoes.roa',
        meta: {
          customPermission: checkPermissionRoa,
          permission: ['gestor'],
          search: {
            title: 'Comissões — ROA',
            description: 'Custódia, receita e ROA por assessor',
            keywords: ['roa', 'custódia', 'receita', 'assessor', 'comissões'],
            group: SEARCH_GROUPS.GESTAO_ESCRITORIOS,
          },
        },
        component: () =>
          lazyLoadView(
            import('@modules/comissoes/views/view-roa/view-roa.vue')
          ),
      },
      {
        path: 'ia',
        name: 'comissoes.ia',
        meta: {
          customPermission: checkPermissionIa,
          search: {
            title: 'Comissões — IA',
            description:
              'Converse com o Especialista em Comissionamento sobre o fechamento',
            keywords: [
              'ia',
              'agente',
              'chat',
              'comissões',
              'fechamento',
              'regras',
            ],
            group: SEARCH_GROUPS.GESTAO_ESCRITORIOS,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/comissoes/views/view-comissoes-ia/view-comissoes-ia.vue'
            )
          ),
      },
    ],
  },
]
